import { useEffect, useState, type FormEvent } from 'react';
import { IconPlus, IconRefresh, IconTrash } from '../components/admin/icons';
import { IconEye, IconPencil } from '../components/ui/icons';
import { deviceModelsApi, type DeviceModel, type DeviceModelCategory, type DeviceModelInput } from '../api/deviceModels';
import { reloadDeviceModels } from '../hooks/useDeviceModels';
import { deviceModelName } from '../i18n/defaultNames';
import { useI18n, type TranslationKey } from '../i18n';

const CATEGORY_KEYS: Record<DeviceModelCategory, TranslationKey> = {
  charger: 'adm_dm_cat_charger',
  socket: 'adm_dm_cat_socket',
  other: 'adm_dm_cat_misc',
};

const EMPTY: DeviceModelInput = { code: '', name: '', nameEn: '', nameHi: '', icon: '', imageUrl: '', category: 'other', sortOrder: 100, active: true };

// Formulaire ouvert : nouveau modèle, modification d'un modèle, ou renommage d'un modèle commun pour l'organisation.
type FormKind = 'new' | 'edit' | 'rename';

/**
 * Catalogue des modèles d'appareils proposés dans l'éditeur d'architecture (« Choisir un appareil »).
 *  - mode « platform » (console super admin) : la liste commune à toutes les organisations ;
 *  - mode « org » (org admin) : la liste de son organisation — modèles communs renommés ou masqués
 *    pour elle seule, et ses propres modèles.
 * Seuls les modèles sont gérés ici ; le numéro de série reste affecté à chaque appareil dans le schéma.
 */
export default function AdminDeviceModelsPage({ mode = 'platform' }: { mode?: 'platform' | 'org' }) {
  const { t, language } = useI18n();
  const isOrg = mode === 'org';
  const [models, setModels] = useState<DeviceModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<DeviceModel | null>(null);
  const [kind, setKind] = useState<FormKind>('new');
  const [form, setForm] = useState<DeviceModelInput | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    deviceModelsApi.list(true)
      .then(list => setModels(isOrg ? list : list.filter(m => m.scope === 'common')))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [mode]);

  const reset = () => { setError(''); setNotice(''); };
  const openNew = () => { setEditing(null); setKind('new'); setForm({ ...EMPTY }); reset(); };
  const openEdit = (m: DeviceModel) => {
    setEditing(m); setKind('edit');
    setForm({ name: m.name, nameEn: m.nameEn ?? '', nameHi: m.nameHi ?? '', icon: m.icon ?? '', imageUrl: m.imageUrl ?? '', category: m.category, sortOrder: m.sortOrder, active: m.active });
    reset();
  };
  const openRename = (m: DeviceModel) => {
    setEditing(m); setKind('rename');
    setForm({ name: m.name, nameEn: m.nameEn ?? '', nameHi: m.nameHi ?? '' });
    reset();
  };
  const close = () => { setForm(null); setEditing(null); };
  const set = <K extends keyof DeviceModelInput>(k: K, v: DeviceModelInput[K]) => setForm(f => (f ? { ...f, [k]: v } : f));

  // Après chaque changement, l'éditeur d'architecture recharge le catalogue.
  const done = (msg: string) => { setNotice(msg); close(); load(); reloadDeviceModels(); };
  const fail = (err: any) => setError(err?.status === 409 && /utilis|used/i.test(err.message) ? t('adm_dm_in_use') : err.message);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaving(true); setError('');
    try {
      const { code, ...data } = form;
      if (kind === 'rename' && editing) {
        await deviceModelsApi.updateForOrg(editing.id, { name: form.name, nameEn: form.nameEn, nameHi: form.nameHi });
      } else if (kind === 'edit' && editing) {
        await (isOrg ? deviceModelsApi.updateForOrg(editing.id, data) : deviceModelsApi.update(editing.id, data));
      } else {
        await (isOrg ? deviceModelsApi.createForOrg(data) : deviceModelsApi.create({ ...data, code }));
      }
      done(editing && editing.name !== form.name ? `${t('adm_dm_saved')} ${t('adm_dm_rename_note')}` : t('adm_dm_saved'));
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  // Modèle commun vu par une organisation : masqué / affiché ; sinon actif / désactivé.
  const toggle = async (m: DeviceModel) => {
    try {
      if (isOrg && m.scope === 'common') await deviceModelsApi.updateForOrg(m.id, { hidden: !m.hidden });
      else if (isOrg) await deviceModelsApi.updateForOrg(m.id, { active: !m.active });
      else await deviceModelsApi.update(m.id, { active: !m.active });
      done(t('adm_dm_saved'));
    } catch (err) { fail(err); }
  };

  const remove = async (m: DeviceModel) => {
    if (!window.confirm(t('adm_dm_delete_confirm', { name: m.name }))) return;
    try { await (isOrg ? deviceModelsApi.removeForOrg(m.id) : deviceModelsApi.remove(m.id)); done(t('adm_dm_deleted')); }
    catch (err) { fail(err); }
  };

  const restore = async (m: DeviceModel) => {
    try { await deviceModelsApi.removeForOrg(m.id); done(t('adm_dm_reset_done')); }
    catch (err) { fail(err); }
  };

  // Image importée depuis le PC : lue en data URL, enregistrée avec le modèle (200 Ko max).
  const IMAGE_MAX_KB = 200;
  const importImage = (file: File) => {
    setError('');
    if (!/^image\/(svg\+xml|png|webp|jpeg)$/.test(file.type)) return setError(t('adm_dm_img_bad_type'));
    if (file.size > IMAGE_MAX_KB * 1024) return setError(t('adm_dm_img_too_big', { max: IMAGE_MAX_KB }));
    const reader = new FileReader();
    reader.onload = () => set('imageUrl', String(reader.result || ''));
    reader.onerror = () => setError(t('adm_dm_img_read_error'));
    reader.readAsDataURL(file);
  };
  const isUploaded = !!form?.imageUrl?.startsWith('data:');

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none';
  const showCode = !isOrg;                  // le code d'un modèle d'organisation est généré
  const fullForm = kind !== 'rename';       // le renommage d'un modèle commun ne porte que sur les noms

  // Fond de la carte selon la catégorie du modèle
  const ART: Record<string, string> = {
    charger: 'bg-gradient-to-br from-amber-50 to-orange-100',
    socket: 'bg-gradient-to-br from-sky-50 to-emerald-100',
    other: 'bg-gradient-to-br from-violet-50 to-violet-100',
  };

  // Chiffres du bandeau, calculés sur la liste chargée
  const commons = models.filter(m => m.scope === 'common');
  const owns = models.filter(m => m.scope === 'org');
  const renamedList = models.filter(m => m.scope === 'common' && m.baseName && m.baseName !== m.name);
  const stats = isOrg ? [
    { label: t('adm_dm_st_offered'), value: models.filter(m => m.active && !m.hidden).length, foot: t('adm_dm_st_offered_foot', { common: commons.length, own: owns.length }) },
    { label: t('adm_dm_st_renamed'), value: renamedList.length, foot: renamedList.map(m => m.name).join(', ') || '—' },
    { label: t('adm_dm_st_hidden'), value: models.filter(m => m.hidden).length, foot: t('adm_dm_st_hidden_foot') },
    { label: t('adm_dm_st_own'), value: owns.length, foot: t('adm_dm_st_own_foot') },
  ] : [
    { label: t('adm_dm_st_total'), value: models.length, foot: t('adm_dm_st_total_foot') },
    { label: t('adm_dm_st_active'), value: models.filter(m => m.active).length, foot: t('adm_dm_st_active_foot') },
    { label: t('adm_dm_st_disabled'), value: models.filter(m => !m.active).length, foot: t('adm_dm_st_disabled_foot') },
    { label: t('adm_dm_st_with_image'), value: models.filter(m => m.imageUrl).length, foot: t('adm_dm_st_with_image_foot') },
  ];

  const status = (m: DeviceModel) => {
    if (isOrg && m.scope === 'common') {
      if (!m.active) return { on: false, label: t('adm_dm_disabled_by_zce'), clickable: false };
      return { on: !m.hidden, label: m.hidden ? t('adm_dm_hidden') : t('adm_dm_visible'), clickable: true };
    }
    return { on: m.active, label: m.active ? t('adm_dm_active') : t('adm_dm_inactive'), clickable: true };
  };

  return (
    <div className={isOrg ? 'max-w-7xl mx-auto px-4 sm:px-6 py-6' : 'p-6'}>
      {/* Bandeau : titre, ajout et chiffres du catalogue (calculés sur la liste chargée) */}
      <section className="relative overflow-hidden rounded-3xl bg-[#0b2a4d] text-white p-6 mb-6">
        <div aria-hidden className="absolute inset-0" style={{ background: 'radial-gradient(60% 90% at 90% 0%, rgba(167,139,250,.38), transparent 60%), linear-gradient(120deg, #0d3b66, #082040)' }} />
        <div aria-hidden className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="max-w-2xl">
            <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-white">{isOrg ? t('adm_dm_org_title') : t('adm_dm_title')}</h1>
            <p className="text-sm text-white/75 mt-1.5 leading-relaxed">{isOrg ? t('adm_dm_org_subtitle') : t('adm_dm_subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-white/25 bg-white/10 text-white hover:bg-white/20" title={t('adm_dm_refresh')} aria-label={t('adm_dm_refresh')}>
              <IconRefresh className="w-4 h-4" />
            </button>
            <button onClick={openNew} className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white text-zce-navy font-bold shadow-md hover:bg-blue-50">
              <IconPlus className="w-4 h-4" /> {t('adm_dm_add')}
            </button>
          </div>
        </div>
        <div className="relative grid gap-3 mt-5 grid-cols-2 lg:grid-cols-4">
          {stats.map(st => (
            <div key={st.label} className="rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3.5 min-w-0">
              <div className="text-xs text-white/75">{st.label}</div>
              <div className="font-mono tabular-nums text-[28px] font-semibold mt-1">{loading ? '—' : st.value}</div>
              <div className="text-[11px] text-white/60 mt-0.5 truncate">{st.foot}</div>
            </div>
          ))}
        </div>
      </section>

      {notice && <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">{notice}</div>}
      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>}

      {form && (
        <form onSubmit={submit} className="mb-6 bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <h2 className="text-base font-bold text-gray-900 mb-1">
            {kind === 'rename' && editing ? t('adm_dm_form_rename', { name: editing.baseName || editing.name })
              : editing ? t('adm_dm_form_edit', { name: editing.name }) : t('adm_dm_form_new')}
          </h2>
          {kind === 'rename' && <p className="text-xs text-gray-500 mb-4">{t('adm_dm_rename_note')}</p>}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${kind === 'rename' ? '' : 'mt-3'}`}>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_name')} *</span>
              <input required maxLength={100} value={form.name ?? ''} onChange={e => set('name', e.target.value)} className={inputCls} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_name_en')}</span>
              <input maxLength={100} value={form.nameEn ?? ''} onChange={e => set('nameEn', e.target.value)} className={inputCls} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_name_hi')}</span>
              <input maxLength={100} value={form.nameHi ?? ''} onChange={e => set('nameHi', e.target.value)} className={inputCls} lang="hi" />
            </label>
            {fullForm && (
              <>
                {showCode && (
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_code')}{!editing && ' *'}</span>
                    <input required={!editing} disabled={!!editing} pattern="[a-z0-9][a-z0-9-]{1,39}" maxLength={40}
                      value={editing ? editing.code : form.code ?? ''} onChange={e => set('code', e.target.value.toLowerCase())}
                      className={`${inputCls} font-mono disabled:bg-gray-100 disabled:text-gray-500`} />
                    <span className="block text-[11px] text-gray-400 mt-1">{t('adm_dm_f_code_hint')}</span>
                  </label>
                )}
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_th_category')}</span>
                  <select value={form.category} onChange={e => set('category', e.target.value as DeviceModelCategory)} className={inputCls}>
                    {(Object.keys(CATEGORY_KEYS) as DeviceModelCategory[]).map(c => <option key={c} value={c}>{t(CATEGORY_KEYS[c])}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_order')}</span>
                  <input type="number" min={0} max={100000} value={form.sortOrder ?? 100} onChange={e => set('sortOrder', Number(e.target.value))} className={inputCls} />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_icon')}</span>
                  <input maxLength={16} value={form.icon ?? ''} onChange={e => set('icon', e.target.value)} className={inputCls} placeholder="🔌" />
                </label>
                <div className="block md:col-span-2">
                  <span className="block text-xs font-medium text-gray-700 mb-1">{t('adm_dm_f_image')}</span>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 shrink-0 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center" title={t('adm_dm_preview')}>
                      {form.imageUrl ? <img src={form.imageUrl} alt="" className="w-8 h-8 object-contain" /> : <span className="text-lg">{form.icon || '⚙️'}</span>}
                    </span>
                    <label className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 cursor-pointer">
                      <IconPlus className="w-4 h-4" /> {t('adm_dm_f_image_upload')}
                      <input type="file" accept="image/svg+xml,image/png,image/webp,image/jpeg" className="sr-only" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importImage(f); }} />
                    </label>
                    {isUploaded ? (
                      <>
                        <span className="text-xs text-gray-500">{t('adm_dm_f_image_uploaded', { size: Math.max(1, Math.round((form.imageUrl?.length ?? 0) * 3 / 4 / 1024)) })}</span>
                        <button type="button" onClick={() => set('imageUrl', '')} className="text-xs font-medium text-red-600 hover:underline">{t('adm_dm_f_image_remove')}</button>
                      </>
                    ) : (
                      <input maxLength={500} value={form.imageUrl ?? ''} onChange={e => set('imageUrl', e.target.value)} className={inputCls} placeholder={t('adm_dm_f_image_url_ph')} />
                    )}
                  </div>
                  <span className="block text-[11px] text-gray-400 mt-1">{t('adm_dm_f_image_hint')}</span>
                </div>
              </>
            )}
          </div>
          {fullForm && (
            <label className="mt-4 inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form.active} onChange={e => set('active', e.target.checked)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
              {t('adm_dm_f_active')}
            </label>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={close} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">{t('adm_dm_cancel')}</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50">{t('adm_dm_save')}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" /></div>
      ) : (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(min(100%,250px),1fr))]">
          {models.map(m => {
            const st = status(m);
            const common = m.scope === 'common';
            const art = ART[m.category] ?? ART.other;
            const renamed = isOrg && common && !!m.baseName && m.baseName !== m.name;
            return (
              <div key={m.id} className={`rounded-2xl bg-white border border-gray-200 overflow-hidden flex flex-col transition-all hover:-translate-y-0.5 hover:shadow-lg ${st.on ? '' : 'opacity-60'}`}>
                <div className={`relative h-36 flex items-center justify-center ${art}`}>
                  {m.imageUrl ? <img src={m.imageUrl} alt="" className="w-20 h-20 object-contain drop-shadow" /> : <span className="text-5xl">{m.icon || '⚙️'}</span>}
                  {isOrg ? (
                    <span className={`absolute top-3 left-3 h-6 px-2.5 rounded-full inline-flex items-center text-[11px] font-bold ${common ? 'bg-white/90 text-zce-navy' : 'bg-violet-100 text-violet-700'}`}>
                      {common ? t('adm_dm_origin_common') : t('adm_dm_origin_org')}
                    </span>
                  ) : (
                    <span className="absolute top-3 left-3 h-6 px-2.5 rounded-full inline-flex items-center font-mono text-[11px] font-semibold bg-white/90 text-zce-navy">{m.code}</span>
                  )}
                </div>
                <div className="p-4 flex flex-col gap-1 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-[15px] text-gray-900 truncate">{deviceModelName(m, language)}</span>
                    <span className={`shrink-0 h-5 px-2 rounded-full inline-flex items-center text-[11px] font-semibold ${renamed ? 'bg-blue-50 text-blue-700' : st.on ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                      {renamed && st.on ? t('adm_dm_renamed') : st.label}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{t(CATEGORY_KEYS[m.category] ?? 'adm_dm_cat_misc')}{renamed ? ` · ${t('adm_dm_common_name', { name: m.baseName ?? '' })}` : ''}</div>
                  {(m.nameEn || m.nameHi) && <div className="text-[11px] text-gray-400 truncate">{[m.nameEn && `EN · ${m.nameEn}`, m.nameHi && `HI · ${m.nameHi}`].filter(Boolean).join('   ')}</div>}
                  {isOrg && <div className="font-mono text-[11px] text-gray-400">{m.code}</div>}
                  {!isOrg && m.formerNames.length > 0 && <div className="text-[11px] text-gray-400 truncate">{t('adm_dm_former', { names: m.formerNames.join(', ') })}</div>}
                  <div className="flex gap-2 mt-auto pt-3">
                    {isOrg && common ? (
                      <>
                        {m.customized
                          ? <button onClick={() => restore(m)} className="flex-1 h-9 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50">{t('adm_dm_reset')}</button>
                          : <button onClick={() => openRename(m)} className="flex-1 h-9 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-1.5"><IconPencil />{t('adm_dm_rename')}</button>}
                        {m.customized && <button onClick={() => openRename(m)} className="w-9 h-9 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 inline-flex items-center justify-center" title={t('adm_dm_rename')} aria-label={t('adm_dm_rename')}><IconPencil /></button>}
                      </>
                    ) : (
                      <>
                        <button onClick={() => openEdit(m)} className="flex-1 h-9 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 inline-flex items-center justify-center gap-1.5"><IconPencil />{t('adm_dm_edit')}</button>
                        <button onClick={() => remove(m)} className="w-9 h-9 rounded-lg border border-gray-200 bg-white text-red-600 hover:bg-red-50 inline-flex items-center justify-center" title={t('adm_dm_delete')} aria-label={t('adm_dm_delete')}><IconTrash className="w-4 h-4" /></button>
                      </>
                    )}
                    <button onClick={() => st.clickable && toggle(m)} disabled={!st.clickable} aria-pressed={st.on}
                      className={`w-9 h-9 rounded-lg border inline-flex items-center justify-center disabled:cursor-default ${st.on ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-gray-200 bg-white text-gray-400 hover:bg-gray-50'}`}
                      title={isOrg && common ? t('adm_dm_toggle_hidden') : t('adm_dm_f_active')} aria-label={isOrg && common ? t('adm_dm_toggle_hidden') : t('adm_dm_f_active')}>
                      <IconEye />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <button onClick={openNew} className="min-h-[280px] rounded-2xl border-2 border-dashed border-gray-300 bg-transparent text-gray-500 flex flex-col items-center justify-center gap-2.5 hover:border-blue-500 hover:bg-blue-50">
            <span className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><IconPlus className="w-6 h-6" /></span>
            <span className="font-bold text-zce-navy">{t('adm_dm_new_card')}</span>
            <span className="text-xs">{t('adm_dm_new_card_hint')}</span>
          </button>
        </div>
      )}
    </div>
  );
}
